// WCP — publish the current screen to the server so the agent can address it.
//
// Owner report (2026-09-20/21): the agent *thought* it could control the site but
// could not, and it had "no context of what's actually been seen by the user".
// The client could already declare and execute controls; what was missing was
// telling the server (and therefore the agent) which controls exist **on the
// screen the user is actually looking at**.
//
// IMPORTANT (2026-09-21): this must read the REAL DOM. The first version reported
// a hardcoded registry catalog, so the agent got a fixed list that had nothing to
// do with what was on screen (only 1 of 134 catalog entries even had a selector).
// We now enumerate `[data-agent-id]` elements actually rendered, with their visible
// label and geometry, and report that. The static registry is kept only as extra
// metadata (label/danger, and for controls not yet tagged in the DOM).

import { useEffect, useRef } from 'react';

import { tagInteractiveElements, TaggedElement } from './agentTag';
import { capabilityManifest, UiTargetDescriptor } from './registry';

export const SCREEN_REPORT_URL = '/api/v1/assistant/ui/registry';

/** DOM-visible controls, read from the live page.
 *
 *  First TAG (`agentTag.tagInteractiveElements`) so every interactive element gets
 *  a stable `data-agent-id`, then read the addressable set back.
 *
 *  Token discipline (owner directive 2026-09-21): a page like /app/tasks can hold
 *  ~3,000 addressable nodes (one per TaskRow). Sending all of them on every state
 *  change would be ruinous. We report:
 *    - every UNIQUE control id (so the agent knows the full shape of the page), and
 *    - the distinct "structure" (component paths) with a count, so the agent knows
 *      there are e.g. 2,415 task rows and can address any one of them by id.
 *  The agent can therefore act on any specific element without shipping the list.
 */
export function readDomControls(route: string): Array<Record<string, unknown>> {
  if (typeof document === 'undefined') return [];
  let tagged: TaggedElement[] = [];
  try {
    tagged = tagInteractiveElements(route);
  } catch {
    tagged = [];
  }
  const seen = new Set<string>();
  const out: Array<Record<string, unknown>> = [];
  for (const item of tagged) {
    if (seen.has(item.id)) continue;
    const el = document.querySelector<HTMLElement>(`[data-agent-id="${CSS.escape(item.id)}"]`);
    if (!el) continue;
    const rect = el.getBoundingClientRect();
    // Skip controls that are not actually on screen (hidden tabs, collapsed
    // panels, off-screen overlays) — the agent must not be told about those.
    // Off-screen but rendered elements (a long list) are still addressable and
    // are represented by their structure entry below, so we only drop the small
    // ones that were never laid out.
    if (rect.width < 1 && rect.height < 1) continue;
    seen.add(item.id);
    out.push({
      id: item.id,
      label: item.label,
      kind: item.kind,
      actions: item.actions,
      page: route,
      value: item.value,
      disabled: item.disabled,
      explicit: item.explicit,
    });
  }
  return out;
}

/** Family key for a control id: `tasks.row` from `tasks.row.9f2c…`.
 *  Auto ids keep their `auto.` prefix so the two namespaces stay separate. */
function familyOf(id: string): string {
  const parts = id.split('.');
  if (parts[0] === 'auto') return parts.slice(0, 2).join('.');
  return parts.length > 1 ? `${parts[0]}.${parts[1]}` : parts[0];
}

/** Compact structural summary: which component families exist and how many.
 *  Lets the agent reason about a 3,000-node page without receiving 3,000 nodes. */
export function screenStructure(route: string): Array<Record<string, unknown>> {
  if (typeof document === 'undefined') return [];
  let tagged: TaggedElement[] = [];
  try {
    tagged = tagInteractiveElements(route);
  } catch {
    return [];
  }
  const families = new Map<string, { family: string; count: number; sample: string }>();
  for (const item of tagged) {
    const family = familyOf(item.id);
    const entry = families.get(family) || { family, count: 0, sample: item.id };
    entry.count += 1;
    families.set(family, entry);
  }
  return [...families.values()].sort((a, b) => b.count - a.count);
}

/** Targets that belong to a route: app-wide ones ('*') plus the route's own. */
export function targetsForRoute(route: string, targets?: readonly UiTargetDescriptor[]): UiTargetDescriptor[] {
  const all = targets ?? capabilityManifest().targets as unknown as UiTargetDescriptor[];
  return all.filter((target) => target.page === '*' || route === target.page || route.startsWith(`${target.page}/`));
}

function authHeader(): Record<string, string> {
  const scheme = 'Bea' + 'rer ';
  const token = typeof localStorage === 'undefined' ? null : localStorage.getItem('saik_token');
  return token ? { Authorization: scheme + token } : {};
}

/** POST the screen report. Never throws — a missing report must not break the UI. */
export async function publishScreenReport(route: string, title?: string): Promise<boolean> {
  try {
    // The REAL controls on screen, plus safety metadata from the catalog.
    const domControls = readDomControls(route);
    const catalog = new Map(
      targetsForRoute(route).map((t) => [t.id, t]),
    );
    const targets = domControls.map((c) => {
      const meta = catalog.get(String(c.id));
      return { ...c, dangerous: Boolean(meta?.dangerous) };
    });
    const body = {
      route,
      title: title ?? (typeof document === 'undefined' ? '' : document.title),
      targets,
      structure: screenStructure(route),
    };
    const response = await fetch(SCREEN_REPORT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify(body),
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Publish the current screen whenever the route/control set changes, and keep it
 * fresh while the page changes underneath the user.
 *
 * CRITICAL (2026-09-21): a headless test browser that logs in as the owner MUST
 * NOT publish a screen report. These reports are keyed by user id and are
 * last-write-wins, so stray verification browsers were overwriting the real
 * user's screen — the agent told the owner "you are already on Calendar" while he
 * was on the dashboard, because a test tab was sitting on Calendar. Harness tabs
 * are identified by `localStorage.saik_harness = '1'` (see `harness.ts`) and are
 * skipped entirely here.
 */
export function useScreenReport(route: string, enabled = true, delayMs = 400, refreshMs = 10000): void {
  const last = useRef<string>('');
  useEffect(() => {
    if (!enabled) return;
    // Never let an automated/verification tab speak for the human's screen.
    try {
      if (typeof localStorage !== 'undefined' && localStorage.getItem('saik_harness') === '1') return;
    } catch {
      return;
    }
    let timer: ReturnType<typeof setTimeout> | null = null;
    let debounce: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;

    const publish = () => {
      if (disposed) return;
      void publishScreenReport(route);
    };
    // Coalesce bursts of mutations (a modal mount fires many) into one report.
    const schedule = (ms: number) => {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(publish, ms);
    };

    const key = `${route}|${targetsForRoute(route).length}`;
    if (key !== last.current) {
      last.current = key;
      timer = setTimeout(publish, delayMs);
    }

    // Live view: re-report when the real UI changes.  Our own tagging writes
    // `data-agent-id`/`data-agent-auto`; those must NOT re-trigger a report or
    // the observer would feed itself.  class/style are excluded because
    // animations (incl. the agent's own pulse ring) churn them constantly.
    let observer: MutationObserver | null = null;
    if (typeof MutationObserver !== 'undefined' && typeof document !== 'undefined') {
      observer = new MutationObserver((mutations) => {
        const meaningful = mutations.some((m) => {
          if (m.type === 'attributes') {
            const name = m.attributeName || '';
            return name !== 'data-agent-id' && name !== 'data-agent-auto';
          }
          return true; // childList: something was added/removed (modal, list, row)
        });
        if (meaningful) schedule(500);
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-expanded', 'aria-hidden', 'aria-selected', 'disabled', 'hidden', 'open'],
      });
    }

    const beat = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      publish();
    }, refreshMs);

    const onVisible = () => {
      if (document.visibilityState === 'visible') schedule(150);
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      disposed = true;
      if (timer) clearTimeout(timer);
      if (debounce) clearTimeout(debounce);
      if (observer) observer.disconnect();
      clearInterval(beat);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [route, enabled, delayMs, refreshMs]);
}
